import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Article } from '../types/prismaTypes';
import { Role } from '../types/Role';
import ArticleRow from './ArticleRow';
import { useAuth } from '../contexts/AuthContext';
import UploadArticle from '../pages/UploadArticle';
import { Helmet } from 'react-helmet-async';
import './ArticleList.css';
import { apiClient } from '../services/apiClient';
import { useLocation } from 'react-router-dom';

const ITEMS_PER_PAGE = 20;

export default function ArticleList() {
  const { user } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalArticles, setTotalArticles] = useState(0);
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const location = useLocation();
  const topRef = useRef<HTMLDivElement>(null);

  // Converter o role do usuário para o tipo correto
  const userRole = user?.role as Role | undefined;

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Se houver termo de busca, usar a rota de busca
      if (searchTerm) {
        const searchParams = new URLSearchParams({
          term: searchTerm,
          page: page.toString(),
          limit: ITEMS_PER_PAGE.toString()
        });

        const response = await apiClient.fetch(`/articles/search?${searchParams.toString()}`);
        
        if (response.articles) {
          setArticles(prevArticles => 
            page === 1 ? response.articles : [...prevArticles, ...response.articles]
          );
          setHasMore(response.articles.length === ITEMS_PER_PAGE);
          setTotalArticles(response.total || 0);
        }
      } else {
        // Se não houver termo de busca, usar a rota normal de listagem
        const response = await apiClient.fetch(`/articles?page=${page}&limit=${ITEMS_PER_PAGE}`);
        
        if (response.articles) {
          setArticles(prevArticles => 
            page === 1 ? response.articles : [...prevArticles, ...response.articles]
          );
          setHasMore(response.articles.length === ITEMS_PER_PAGE);
          setTotalArticles(response.pagination?.total || 0);
        }
      }

      console.log('Dados recebidos:', {
        page,
        searchTerm,
        totalArticles,
        hasMore
      });

    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm]);

  // Efeito para configurar o observer
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 0.5 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading]);

  // Efeito para carregar artigos quando a página mudar
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Monitorar mudanças na rota para atualizar a lista
  useEffect(() => {
    if (!showUpload) {
      fetchArticles();
    }
  }, [showUpload, location]);

  // Adicione um novo useEffect para monitorar mudanças no searchTerm
  useEffect(() => {
    setPage(1);
    setArticles([]);
    fetchArticles();
  }, [searchTerm]); // Dependência apenas no searchTerm

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempSearchTerm(value);
    
    // Se o campo estiver vazio, faz a busca automática
    if (value === '') {
      setSearchTerm('');
      setPage(1);
      setArticles([]);
    }
  };

  const handleSearch = () => {
    setSearchTerm(tempSearchTerm);
    setPage(1);
    setArticles([]);
    fetchArticles();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/+$/, ''); // Remove trailing slashes
      console.log('Deletando artigo:', { id, apiUrl });

      const response = await fetch(`${apiUrl}/articles/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        credentials: 'include'
      });

      console.log('Resposta da deleção:', {
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        // Tenta pegar o erro como JSON primeiro
        const errorData = await response.json().catch(() => null);
        
        if (errorData) {
          throw new Error(errorData.message || 'Erro ao excluir artigo');
        } else {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
      }

      // Atualiza a lista após deletar
      setArticles(prev => prev.filter(article => article.id !== id));
      setTotalArticles(prev => prev - 1);
      
    } catch (error) {
      console.error('Erro ao excluir:', error);
      throw error;
    }
  };

  const handleUpdate = async (article: Article) => {
    try {
      console.log('🔄 Tentando atualizar artigo:', {
        id: article.id,
        hasToken: !!localStorage.getItem('auth_token'),
        tokenPreview: localStorage.getItem('auth_token')?.substring(0, 10) + '...'
      });

      await apiClient.articles.update(article.id, article);
      
      // Atualizar lista local
      setArticles(prev => 
        prev.map(a => a.id === article.id ? article : a)
      );
    } catch (error) {
      console.error('❌ Erro ao atualizar:', error);
      throw error;
    }
  };

  return (
    <div className="article-list-page" ref={topRef}>
      <Helmet>
        <title>Biblioteca MD Ensino</title>
        <meta name="description" content="Banco de Artigos do Paciente Grave" />
      </Helmet>
      <div className="article-list-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por título, revista ou palavras-chave..."
            value={tempSearchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button" aria-label="Buscar">
            🔍
          </button>
        </div>

        <div className="articles-count">
          {!loading && !error && (
            <p>Total de artigos: <strong>{totalArticles}</strong></p>
          )}
        </div>

        {loading && articles.length === 0 ? (
          <div className="loading">
            <p>Carregando artigos...</p>
            <div className="loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => fetchArticles()} className="retry-button">
              Tentar novamente
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="no-results">
            <p>{searchTerm ? `Nenhum artigo encontrado para "${searchTerm}"` : 'Nenhum artigo disponível'}</p>
          </div>
        ) : (
          <>
            <div className="articles-list">
              {articles.map(article => (
                <ArticleRow
                  key={article.id}
                  article={article}
                  userRole={userRole}
                  onDelete={handleDelete}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
            {hasMore && (
              <div 
                ref={loadingRef} 
                className="loading-more"
                style={{ height: '20px', margin: '20px 0' }}
              >
                {loading && <div className="loading-spinner" />}
              </div>
            )}
          </>
        )}
      </div>
      {showUpload ? (
        <div className="upload-component">
          <UploadArticle />
          <button 
            onClick={() => setShowUpload(false)}
            className="btn-secondary mt-4"
          >
            Cancelar
          </button>
        </div>
      ) : null}
    </div>
  );
} 